// Pieces the workbench and the demand pool share (spec 03 §4.1, §4.2): the
// order label as module 09 rendered it for the reader, the chips, the unit
// totals and the Refresh demand control with its poll.

import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { StatusChip, type ChipVariant } from "../../../../components/status-chip";
import { Banner } from "../../../../components/toast";
import { formatTimestamp } from "../../../../lib/datetime";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { formatGridQty } from "../../../../components/bucket-grid-paste";
import type { Product } from "../hooks/planning-queries";
import {
  requestDemandRefresh,
  useDemandRefresh,
  useDemandRefreshAll,
  type DemandDateBasis,
  type DemandEntry,
  type DemandRefresh,
  type DemandSourceKind,
  type SalesOrderLabel,
} from "../hooks/demand-queries";
import { useOffline } from "../lib/planning-lib";

export const REFRESH_STATUS_VARIANT: Readonly<Record<DemandRefresh["status"], ChipVariant>> = {
  running: "accent",
  succeeded: "ok",
  failed: "bad",
};

export const SOURCE_VARIANT: Readonly<Record<DemandSourceKind, ChipVariant>> = {
  sales_order: "accent",
  forecast: "neutral",
  safety_stock: "warn",
  transfer: "neutral",
};

/** The words of an order label: number, line and customer, or "Sales order" (D-349 ruling 15). */
export function orderLabelText(label: SalesOrderLabel | null | undefined, t: TFunction): string {
  if (label?.order_no == null) {
    return t("planning.demand.salesOrder");
  }
  const parts = [label.order_no];
  if (label.line_no !== null) parts.push(t("planning.demand.lineNo", { line: label.line_no }));
  if (label.customer_name !== null) parts.push(label.customer_name);
  return parts.join(" · ");
}

/** The label, linked to the order when the reader may open it. */
export function OrderLabel({ label, testId }: { label: SalesOrderLabel | null; testId?: string }) {
  const { t } = useTranslation();
  const text = orderLabelText(label, t);
  if (label?.order_id) {
    return (
      <Link
        to="/sales/orders/$orderId"
        params={{ orderId: label.order_id }}
        data-testid={testId}
        data-linked="true"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {text}
      </Link>
    );
  }
  return (
    <span data-testid={testId} data-linked="false">
      {text}
    </span>
  );
}

/**
 * A transfer entry's destination order (§4.2, D-387 ruling 6, D-395 ruling
 * 70): the destination plant to every reader, the PLN number to a reader of
 * planned orders, and a link to the order's drawer only when the server says
 * the reader can open it, which is also the only time it serves the id.
 */
export function TransferLabel({
  transfer,
  orderId,
  testId,
}: {
  transfer: DemandEntry["transfer"];
  orderId: string | null;
  testId?: string;
}) {
  const { t } = useTranslation();
  if (transfer === null) return null;
  const facility = transfer.destination_facility_name;
  const text =
    transfer.po_no === null
      ? t("planning.demand.transferTo", { facility })
      : t("planning.demand.transferOrder", { number: transfer.po_no, facility });
  if (transfer.linkable && orderId !== null) {
    return (
      <Link
        to="/planning/orders"
        search={{ order: orderId }}
        data-testid={testId}
        data-linked="true"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {text}
      </Link>
    );
  }
  return (
    <span data-testid={testId} data-linked="false">
      {text}
    </span>
  );
}

export function TriggerChip({ trigger }: { trigger: DemandRefresh["trigger"] }) {
  const { t } = useTranslation();
  return (
    <StatusChip
      variant="neutral"
      dot={false}
      data-status={trigger}
      data-testid="planning-refresh-trigger"
    >
      {t(`planning.demand.trigger.${trigger}`)}
    </StatusChip>
  );
}

export function RefreshStatusChip({ status }: { status: DemandRefresh["status"] }) {
  const { t } = useTranslation();
  return (
    <StatusChip variant={REFRESH_STATUS_VARIANT[status]} data-status={status}>
      {t(`planning.demand.status.${status}`)}
    </StatusChip>
  );
}

export function DateBasisChip({ basis }: { basis: DemandDateBasis }) {
  const { t } = useTranslation();
  if (basis === "line_promise") return null;
  return (
    <StatusChip
      variant={basis === "past_due" ? "bad" : "warn"}
      dot={false}
      data-status={basis}
      data-testid="planning-demand-basis"
    >
      {t(`planning.demand.basis.${basis}`)}
    </StatusChip>
  );
}

const THOUSANDTHS = /^(\d+)(?:\.(\d{1,3}))?$/u;

/** A three-decimal quantity string as thousandths, so a sum never touches a float. */
export function qtyThousandths(qty: string): bigint {
  const m = THOUSANDTHS.exec(qty);
  if (m === null) return 0n;
  return BigInt(m[1] ?? "0") * 1000n + BigInt((m[2] ?? "").padEnd(3, "0"));
}

export function thousandthsQty(n: bigint): string {
  const whole = n / 1000n;
  const frac = (n % 1000n).toString().padStart(3, "0");
  return `${whole.toString()}.${frac}`;
}

export interface UnitTotal {
  readonly uomId: string;
  readonly qty: string;
}

/**
 * Sums quantities per base unit, because a sum across units means nothing
 * (spec 03 §4.1). A product whose card has not answered yet is left out and
 * counted, so the caller can say the figure is partial.
 */
export function totalsByUnit(
  rows: readonly { product_id: string; qty: string }[],
  products: ReadonlyMap<string, Product>,
): { totals: UnitTotal[]; unknown: number } {
  const sums = new Map<string, bigint>();
  let unknown = 0;
  for (const row of rows) {
    const product = products.get(row.product_id);
    if (product === undefined) {
      unknown += 1;
      continue;
    }
    sums.set(product.base_uom_id, (sums.get(product.base_uom_id) ?? 0n) + qtyThousandths(row.qty));
  }
  const totals = [...sums.entries()]
    .filter(([, n]) => n > 0n)
    .map(([uomId, n]) => ({ uomId, qty: thousandthsQty(n) }))
    .sort((a, b) => a.uomId.localeCompare(b.uomId));
  return { totals, unknown };
}

/** A quantity with its unit code, "12 500 m". */
export function QtyWithUnit({
  qty,
  unit,
  testId,
}: {
  qty: string;
  unit: string | undefined;
  testId?: string;
}) {
  const { i18n } = useTranslation();
  return (
    <span className="num" data-testid={testId} data-qty={qty}>
      {formatGridQty(qty, i18n.language)}
      {unit !== undefined ? <span className="planning-unit"> {unit}</span> : null}
    </span>
  );
}

export interface DemandRefreshControl {
  /** The run the reader started and is waiting on, while it runs. */
  readonly running: boolean;
  readonly start: () => void;
  readonly button: ReactNode;
  readonly banner: ReactNode;
}

/**
 * Refresh demand (`planning.demand.manage`): claims a run, polls it until it
 * ends, then re-reads every demand read of the facility. A run already going
 * at the facility is answered with its id and watched the same way.
 */
export function useDemandRefreshControl(
  facilityId: string,
  timezone: string | null | undefined,
  options: { canRefresh: boolean; testId?: string; label?: string },
): DemandRefreshControl {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const offline = useOffline();
  const refreshAll = useDemandRefreshAll();
  const [watching, setWatching] = useState<string | undefined>(undefined);
  const [starting, setStarting] = useState(false);
  const [failure, setFailure] = useState<unknown>(undefined);
  const [outcome, setOutcome] = useState<DemandRefresh | undefined>(undefined);
  const run = useDemandRefresh(watching);

  useEffect(() => {
    setWatching(undefined);
    setOutcome(undefined);
    setFailure(undefined);
  }, [facilityId]);

  const done = run.data !== undefined && run.data.status !== "running" ? run.data : undefined;
  useEffect(() => {
    if (done === undefined) return;
    setOutcome(done);
    setWatching(undefined);
    void refreshAll(facilityId);
  }, [done, facilityId, refreshAll]);

  const start = async () => {
    setStarting(true);
    setFailure(undefined);
    setOutcome(undefined);
    try {
      const accepted = await requestDemandRefresh(facilityId);
      setWatching(accepted.demand_refresh_id);
      void refreshAll(facilityId);
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      setFailure(error);
    } finally {
      setStarting(false);
    }
  };

  const running = starting || watching !== undefined;
  const button = options.canRefresh ? (
    <Button
      variant="primary"
      busy={running}
      busyLabel={t("planning.demand.refreshing")}
      disabled={offline}
      data-testid={options.testId ?? "planning-demand-refresh"}
      onClick={() => void start()}
    >
      {options.label ?? t("planning.demand.refresh")}
    </Button>
  ) : null;

  let banner: ReactNode = null;
  if (failure !== undefined) {
    banner = (
      <Banner kind="bad" data-testid="planning-demand-refresh-failure">
        {t("planning.demand.refreshRequestFailed", { requestId: requestIdOf(failure) ?? "-" })}
      </Banner>
    );
  } else if (outcome?.status === "succeeded") {
    banner = (
      <Banner kind="ok" data-testid="planning-demand-refresh-done">
        {t("planning.demand.refreshDone", {
          time: formatTimestamp(outcome.finished_at, i18n.language, timezone),
        })}
      </Banner>
    );
  } else if (outcome?.status === "failed") {
    banner = (
      <Banner kind="bad" data-testid="planning-demand-refresh-failed">
        {t("planning.demand.refreshFailed", { error: outcome.error ?? "-" })}
      </Banner>
    );
  } else if (offline && options.canRefresh) {
    banner = <p className="planning-note">{t("planning.common.offlineWrites")}</p>;
  }
  return { running, start: () => void start(), button, banner };
}
